import React, { useRef, useState, useLayoutEffect, useEffect } from 'react';
import {
    motion,
    AnimatePresence,
    useScroll,
    useTransform,
    useMotionValueEvent,
    useAnimation,
} from 'framer-motion';
import s from './CareerCard.module.scss';
import Image from '../Image';

const renderTextWithHighlights = (input) =>
    input.split(/(\s+)/).map((chunk, i) =>
        chunk.startsWith('#') ? (
            <span key={i} className={s.Highlight}>
                {chunk.slice(1)}
            </span>
        ) : (
            <span key={i}>{chunk}</span>
        )
    );

const ImageTextCard = ({ image, blocks }) => (
    <div className={s.ImageTextCard}>
        <div className={s.ImageContainer}>
            <div className={s.Background} />
            <div className={s.ClipContainer}>
                <div className={s.Image}>
                    <Image {...image} />
                </div>
            </div>
        </div>
        <div className={s.ImageTextCardContainer}>
            {blocks.map((block, i) => (
                <div key={i} className={s.ImageTextCardTextWrapper}>
                    <span className={s.ImageTextCardText}>{block.text}</span>
                    <h3 className={s.ImageTextCardTitle}>{block.title}</h3>
                </div>
            ))}
        </div>
    </div>
);

const CareerCard = ({
    highlightedMessage = '',
    title = '',
    cardImage,
    infoBlocks = [],
}) => {
    const [isMobile, setIsMobile] = useState(false);

    const scrollRef = useRef(null);
    const itemCount = 2;

    const { scrollYProgress: progressToTop } = useScroll({
        target: scrollRef,
        offset: ['start 80%', 'start 0%'],
    });

    const { scrollY } = useScroll();
    const [pts, setPts] = useState({ start: 0, end: 0 });

    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const topAbs = rect.top + window.scrollY;
        const vh = window.innerHeight;
        const scrollDelay = 200;

        setPts({
            start: topAbs + scrollDelay,
            end: topAbs + rect.height - vh - scrollDelay,
        });
    }, []);

    const carouselProgress = useTransform(
        scrollY,
        [pts.start, pts.end],
        [0, 1],
        { clamp: true }
    );
    const x = useTransform(carouselProgress, [0, 1], ['0vw', '-65vw']);
    const yMobile = useTransform(
        carouselProgress,
        [0, 1],
        ['0%', `-${(itemCount - 1) * 100}%`]
    );
    const firstOpacity = useTransform(carouselProgress, [0, 1], [1, 0.3]);
    const secondOpacity = useTransform(carouselProgress, [0, 1], [0.3, 1]);

    const scale = useTransform(progressToTop, [0, 1], [0.5, 1]);
    const entryX = useTransform(progressToTop, [0, 1], ['70vw', '0vw']);
    const entryY = useTransform(progressToTop, [0, 1], ['10vh', '0vh']);
    const entryOp = useTransform(progressToTop, [0, 1], [0.5, 1]);

    const [showTitle, setShowTitle] = useState(false);
    const [titleKey, setTitleKey] = useState(0);
    const contentControls = useAnimation();
    const hasAnimated = useRef(false);

    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 1024);
        check();
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, []);

    useMotionValueEvent(progressToTop, 'change', (v) => {
        if (v >= 1 && !showTitle) {
            setShowTitle(true);
            setTitleKey((k) => k + 1);
            if (!hasAnimated.current) {
                contentControls.start({
                    opacity: 1,
                    transition: { duration: 1.5 },
                });
                hasAnimated.current = true;
            }
        }
        if (v < 0.95 && showTitle) {
            setShowTitle(false);
        }
    });

    return (
        <div
            className={s.Root}
            ref={scrollRef}
            style={{ height: `${itemCount * 100}vh` }}>
            <motion.div
                className={s.Container}
                style={{ scale, x: entryX, y: entryY, opacity: entryOp }}
                transition={{ type: 'spring', stiffness: 60, damping: 12 }}>
                <div className={s.Inner}>
                    <AnimatePresence>
                        {showTitle && (
                            <motion.h2
                                key={titleKey}
                                className={s.Title}
                                initial={{ y: 200, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                exit={{ y: 200, opacity: 0 }}
                                transition={{
                                    type: 'spring',
                                    stiffness: 60,
                                    damping: 12,
                                }}>
                                {title}
                            </motion.h2>
                        )}
                    </AnimatePresence>

                    <motion.div
                        className={s.Content}
                        style={
                            isMobile
                                ? { y: yMobile, height: `${itemCount * 50}%` }
                                : { x, width: '165vw' }
                        }
                        initial={{ opacity: 0 }}
                        animate={contentControls}>
                        <motion.div
                            className={s.Card}
                            style={{ opacity: firstOpacity }}>
                            <div className={s.TextContainer}>
                                <span className={s.Text}>
                                    {renderTextWithHighlights(
                                        highlightedMessage
                                    )}
                                </span>
                            </div>
                        </motion.div>

                        <motion.div
                            className={s.Card}
                            style={{ opacity: secondOpacity }}>
                            <ImageTextCard
                                image={cardImage}
                                blocks={infoBlocks}
                            />
                        </motion.div>
                    </motion.div>
                </div>
            </motion.div>
        </div>
    );
};

export default CareerCard;
